import React from 'react';
import { motion } from 'framer-motion';
import { User, Briefcase, FileText, Landmark, ShieldCheck, CheckCircle2, ChevronRight, Lock, Pencil, Sparkles, RotateCcw, AlertTriangle, AlertCircle } from 'lucide-react';
interface Section {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
}
interface SectionsOverviewProps {
  completedSections: string[];
  onStartSection: (sectionId: string) => void;
  onFillSampleData?: () => void;
  onResetData?: () => void;
  rejectionState?: {
    isRejected: boolean;
    flaggedSections: string[];
    adminNotes: Record<string, string>;
  };
}
const SECTIONS: Section[] = [{
  id: 'business-info',
  title: 'Business Details',
  description: 'Legal structure, registration, and business activity',
  icon: Briefcase
}, {
  id: 'personal-info',
  title: 'Representative Details',
  description: 'Personal details and identity verification',
  icon: User
}, {
  id: 'documents',
  title: 'Supporting Documents',
  description: 'Upload required legal documentation',
  icon: FileText
}, {
  id: 'bank-account',
  title: 'Link Bank Account',
  description: 'Connect your primary business account',
  icon: Landmark
}, {
  id: 'account-security',
  title: 'Account Security',
  description: 'Set up two-factor authentication',
  icon: ShieldCheck
}, {
  id: 'review',
  title: 'Review & Submit',
  description: 'Final check before submission',
  icon: CheckCircle2
}];
export function SectionsOverview({
  completedSections,
  onStartSection,
  onFillSampleData,
  onResetData,
  rejectionState
}: SectionsOverviewProps) {
  const isSectionComplete = (id: string) => completedSections.includes(id);
  const isSectionUnlocked = (index: number) => {
    if (rejectionState?.isRejected) return true; // In rejection state, all sections are technically unlocked, but we handle interactivity differently
    if (index === 0) return true;
    const prevSectionId = SECTIONS[index - 1].id;
    return isSectionComplete(prevSectionId);
  };
  // The "next" actionable section: first one that's unlocked and incomplete
  const nextActionableIndex = SECTIONS.findIndex((s, i) => isSectionUnlocked(i) && !isSectionComplete(s.id));
  const completedCount = completedSections.filter(id => SECTIONS.some(s => s.id === id)).length;
  const totalCount = SECTIONS.length;
  const percent = Math.round(completedCount / totalCount * 100);
  const isRejected = rejectionState?.isRejected;
  const flaggedSections = rejectionState?.flaggedSections || [];
  const adminNotes = rejectionState?.adminNotes || {};
  return <div className="max-w-xl mx-auto py-4 animate-in fade-in duration-700" data-id="element-840">
      {/* Global progress */}
      {!isRejected && <div className="mb-8" data-id="element-841">
          <div className="flex items-end justify-between mb-2" data-id="element-842">
            <div data-id="element-843">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-844">
                Application Progress
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-845">
                {percent}% Completed
              </p>
            </div>
            <p className="text-sm text-gray-500" data-id="element-846">
              <span className="font-semibold text-gray-900" data-id="element-847">
                {completedCount}
              </span>{' '}
              of {totalCount} sections
            </p>
          </div>
          <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-848">
            <motion.div className="h-full bg-[#FFCC00]" initial={{
          width: 0
        }} animate={{
          width: `${percent}%`
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-849" />
          </div>
        </div>}

      {/* Heading */}
      <div className="mb-6" data-id="element-850">
        {isRejected ? <>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-851">
              Action Required
            </h1>
            <p className="text-gray-600" data-id="element-852">
              Maybank requires a few updates before we can approve your merchant
              account.
            </p>
          </> : <>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-853">
              Let's get you set up
            </h1>
            <p className="text-gray-600" data-id="element-854">
              Please complete the following sections.
            </p>
          </>}
      </div>

      {/* Global Rejection Alert */}
      {isRejected && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-id="element-855">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-856" />
          <p className="text-sm text-red-900 leading-relaxed" data-id="element-857">
            Your application was reviewed on 05 May 2026. Please update the
            flagged sections below to resubmit your application.
          </p>
        </div>}

      {/* Demo controls — for stakeholder walkthroughs */}
      {onFillSampleData && !isRejected && <div className="mb-6 flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-dashed border-gray-300" data-id="element-858">
          <div className="flex items-center gap-2 text-xs text-gray-600 min-w-0" data-id="element-859">
            <Sparkles className="w-4 h-4 text-gray-700 flex-shrink-0" aria-hidden="true" data-id="element-860" />
            <span className="truncate" data-id="element-861">
              Demo mode — skip the forms and jump to Review.
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-862">
            {completedSections.length > 0 && onResetData && <button type="button" onClick={onResetData} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-900 underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded" data-id="element-863">
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" data-id="element-864" />
                Reset
              </button>}
            <button type="button" onClick={onFillSampleData} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs font-semibold hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 transition-colors" data-id="element-865">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" data-id="element-866" />
              Fill sample data
            </button>
          </div>
        </div>}

      {/* Sections */}
      <div className="space-y-3" data-id="element-867">
        {SECTIONS.map((section, index) => {
        const isReviewSection = section.id === 'review';
        const isFlagged = isRejected && flaggedSections.includes(section.id);
        const isApproved = isRejected && !isFlagged && !isReviewSection;
        const isComplete = isSectionComplete(section.id);
        const isUnlocked = isSectionUnlocked(index);
        const Icon = section.icon;
        // Determine state based on rejection mode or normal mode
        let isLocked = !isUnlocked && !isComplete;
        let isNext = index === nextActionableIndex;
        let isInteractive = !isLocked;
        if (isRejected) {
          if (isReviewSection) {
            // Review section is locked until all flagged sections are fixed
            // For demo purposes, we'll just keep it locked if there are flagged sections
            isLocked = flaggedSections.length > 0;
            isInteractive = !isLocked;
            isNext = false;
          } else {
            isLocked = isApproved;
            isInteractive = isFlagged;
            isNext = isFlagged;
          }
        }
        let cardClass = `
            group relative rounded-xl border w-full text-left transition-all duration-300
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8860B] focus-visible:ring-offset-2
          `;
        if (isRejected) {
          if (isFlagged) {
            cardClass += ' bg-red-50/10 border-2 border-red-500 shadow-md ring-4 ring-red-500/10 hover:shadow-lg';
          } else if (isApproved) {
            cardClass += ' bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed';
          } else if (isReviewSection) {
            cardClass += isLocked ? ' bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed' : ' bg-[#FFCC00] border-[#FFCC00] shadow-md hover:shadow-lg';
          }
        } else {
          cardClass += isComplete ? ' bg-white border-gray-300 hover:border-gray-500 hover:bg-gray-50' : isNext ? ' bg-white border-[#FFCC00] shadow-md ring-4 ring-[#FFCC00]/20 hover:shadow-lg' : isUnlocked ? ' bg-white border-gray-300 shadow-sm hover:border-gray-500 hover:shadow-md' : ' bg-gray-50/60 border-gray-100 cursor-not-allowed';
        }
        const cardContent = <div className={`flex flex-col ${isNext || isFlagged ? 'p-5' : 'p-4'}`} data-id="element-868">
              <div className="flex items-start sm:items-center gap-4" data-id="element-869">
                {/* Icon */}
                <div className={`
                    w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 sm:mt-0
                    ${isFlagged ? 'bg-red-100 text-red-600' : isComplete || isApproved ? 'bg-green-100 text-green-700' : isNext ? 'bg-[#FFCC00] text-gray-900' : isUnlocked ? 'bg-[#FFCC00]/15 text-gray-900' : 'bg-gray-100 text-gray-400'}
                  `} data-id="element-870">
                  {isFlagged ? <AlertTriangle className="w-5 h-5" aria-hidden="true" data-id="element-871" /> : isComplete || isApproved ? <CheckCircle2 className="w-5 h-5" aria-hidden="true" data-id="element-872" /> : <Icon className="w-5 h-5" aria-hidden="true" data-id="element-873" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4" data-id="element-874">
                  <div className="flex-1 min-w-0" data-id="element-875">
                    <div className="flex flex-wrap items-center gap-2 mb-0.5" data-id="element-876">
                      <h3 className={`font-semibold text-base ${isLocked ? 'text-gray-400' : isFlagged ? 'text-red-900' : 'text-gray-900'}`} data-id="element-877">
                        {isLocked && <span className="sr-only" data-id="element-878">Locked: </span>}
                        {(isComplete || isApproved) && <span className="sr-only" data-id="element-879">Completed: </span>}
                        {section.title}
                      </h3>
                      {isFlagged && <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-880">
                          Action Required
                        </span>}
                      {!isRejected && isNext && <span className="text-[10px] font-bold uppercase tracking-wider text-gray-900 bg-[#FFCC00] px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-881">
                          Next
                        </span>}
                      {!isRejected && isComplete && <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-882">
                          Completed
                        </span>}
                      {isRejected && isApproved && <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-883">
                          Approved
                        </span>}
                    </div>
                    <p className={`text-sm ${isLocked ? 'text-gray-400' : isFlagged ? 'text-red-700' : 'text-gray-600'}`} data-id="element-884">
                      {section.description}
                    </p>
                  </div>

                  {/* Affordance */}
                  <div className="flex-shrink-0" data-id="element-885">
                    {isFlagged ? <span className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFCC00] text-black font-semibold text-sm group-hover:bg-[#E6B800] transition-colors w-full sm:w-auto" data-id="element-886">
                        Fix Issue
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" data-id="element-887" />
                      </span> : isRejected && isReviewSection && !isLocked ? <span className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold text-sm group-hover:bg-black transition-colors w-full sm:w-auto" data-id="element-888">
                        Review & Resubmit
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" data-id="element-889" />
                      </span> : isRejected && isReviewSection && isLocked ? <div className="p-2 text-gray-300" aria-label="Locked" role="img" data-id="element-890">
                        <Lock className="w-5 h-5" data-id="element-891" />
                      </div> : isApproved ? null : isComplete ?
                // No edit button for approved sections
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 group-hover:text-black" data-id="element-892">
                        <Pencil className="w-4 h-4" aria-hidden="true" data-id="element-893" />
                        Edit
                      </span> : isNext ? <span className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFCC00] text-black font-semibold text-sm group-hover:bg-[#E6B800] transition-colors w-full sm:w-auto" data-id="element-894">
                        {completedCount > 0 ? 'Continue' : 'Start'}
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" data-id="element-895" />
                      </span> : isUnlocked ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 group-hover:text-black" data-id="element-896">
                        Start
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" data-id="element-897" />
                      </span> : <div className="p-2 text-gray-300" aria-label="Locked" role="img" data-id="element-898">
                        <Lock className="w-5 h-5" data-id="element-899" />
                      </div>}
                  </div>
                </div>
              </div>

              {/* Admin Note for Flagged Sections */}
              {isFlagged && adminNotes[section.id] && <div className="mt-4 sm:ml-[60px] p-3 bg-red-50 border-l-4 border-red-500 rounded-r-md" data-id="element-900">
                  <p className="text-sm text-red-900" data-id="element-901">
                    <span className="font-bold text-black" data-id="element-902">
                      Reason for return:
                    </span>{' '}
                    {adminNotes[section.id]}
                  </p>
                </div>}

              {/* Helper text for locked review section */}
              {isRejected && isReviewSection && isLocked && <div className="mt-3 text-center" data-id="element-903">
                  <p className="text-xs text-gray-500" data-id="element-904">
                    Please fix the flagged issues above to resubmit.
                  </p>
                </div>}
            </div>;
        return <motion.div key={section.id} layout initial={false} animate={{
          scale: isNext ? 1 : 0.995
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 26
        }} data-id="element-905">
              {isInteractive ? <button type="button" onClick={() => onStartSection(section.id)} aria-label={isComplete ? `Edit ${section.title} (completed)` : `${completedCount > 0 && isNext ? 'Continue' : 'Start'} ${section.title}`} className={cardClass} data-id="element-906">
                  {cardContent}
                </button> : <div aria-disabled="true" className={cardClass} data-id="element-907">
                  {cardContent}
                </div>}
            </motion.div>;
      })}
      </div>
    </div>;
}